import axios from 'axios'
import { GUEST_LOGGED_IN_USER, LOGGED_IN_USER, NO_USER } from '../constents/app';
import { guestLoginService } from './authService';
import store from '../store/storeModules.js';
import router from '../router';

// Token storage is split by role. The legacy single-slot 'token' key is migrated
// once at module load. The refresh token is intentionally left alone — see
// BACKEND_AUTH_REQUIREMENTS.md item #1 (current refresh JWT has identical expiry
// to the access JWT and no /refresh endpoint exists).
export const GUEST_TOKEN_KEY = 'guestToken';
export const USER_TOKEN_KEY = 'userToken';
const LEGACY_TOKEN_KEY = 'token';

(function migrateLegacyTokenOnce() {
  if (typeof window === 'undefined') return;
  const legacy = window.localStorage.getItem(LEGACY_TOKEN_KEY);
  if (!legacy) return;
  try {
    const type = store.getters['user/LoggedInUserType'];
    const targetKey = type === LOGGED_IN_USER ? USER_TOKEN_KEY : GUEST_TOKEN_KEY;
    if (!window.localStorage.getItem(targetKey)) {
      window.localStorage.setItem(targetKey, legacy);
    }
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch (_) {
    // best-effort migration
  }
})();

function getActiveToken() {
  const type = store.getters['user/LoggedInUserType'];
  if (type === LOGGED_IN_USER) {
    return localStorage.getItem(USER_TOKEN_KEY) || localStorage.getItem(GUEST_TOKEN_KEY);
  }
  return localStorage.getItem(GUEST_TOKEN_KEY);
}

const instance = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_BASE_URL,
  timeout: 30000
});

instance.interceptors.request.use(request => {
  const token = getActiveToken();
  if (token) {
    request.headers['authorization'] = token;
  } else {
    delete request.headers['authorization'];
  }
  request.headers['cid'] = process.env.VUE_APP_CID;
  request.headers['authmode'] = process.env.VUE_APP_AUTHMODE;
  return request;
});

// One in-flight guest refresh shared across concurrent callers, so N parallel
// 400s trigger one /autologinasguest call, not N.
let inflightGuestRefresh = null;

function refreshGuestToken() {
  if (!inflightGuestRefresh) {
    inflightGuestRefresh = guestLoginService()
      .then(resp => {
        if (!resp || !resp.token) {
          throw new Error('Guest login returned no token');
        }
        localStorage.setItem(GUEST_TOKEN_KEY, resp.token);
        if (resp.refreshtoken) {
          localStorage.setItem('refreshtoken', resp.refreshtoken);
        }
        return resp.token;
      })
      .finally(() => {
        inflightGuestRefresh = null;
      });
  }
  return inflightGuestRefresh;
}

const JWT_ERROR_CODES = new Set([
  'JWT_EXPIRED',
  'JWT_MALFORMED',
  'AUTH_AUTHORIZATION_MISSED',
]);

function isJwtAuthError(error) {
  const status = error && error.response && error.response.status;
  const code = error && error.response && error.response.data && error.response.data.code;
  return status === 400 && JWT_ERROR_CODES.has(code);
}

function isGuestLoginEndpoint(config) {
  const url = (config && config.url) || '';
  return url.indexOf('/userprofile/autologinasguest') !== -1;
}

instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (!isJwtAuthError(error) || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If the guest-login endpoint itself returned a JWT error, we cannot recover
    // by calling it again. Clear both tokens and surface the error.
    if (isGuestLoginEndpoint(originalRequest)) {
      localStorage.removeItem(GUEST_TOKEN_KEY);
      localStorage.removeItem(USER_TOKEN_KEY);
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const previousUserType = store.getters['user/LoggedInUserType'];

    if (previousUserType === LOGGED_IN_USER) {
      // User session expired. No silent downgrade: clear user token, restore a
      // guest token so browsing keeps working, then redirect to login with a
      // clear reason. The original request is NOT retried — the caller must
      // re-issue it after the user re-authenticates.
      localStorage.removeItem(USER_TOKEN_KEY);
      // Cart/wishlist were owned by the expiring user. The backend rejects
      // cross-owner writes with OPERATION_DENIED, so the new guest cannot
      // continue operating on them.
      localStorage.removeItem('carrelloId');
      store.commit('resetCart');
      store.commit('wishlist/resetWishlist');
      try {
        await refreshGuestToken();
        store.dispatch('user/updateLoggedInUserType', GUEST_LOGGED_IN_USER);
      } catch (_) {
        store.dispatch('user/updateLoggedInUserType', NO_USER);
      }

      const currentPath = router.currentRoute && router.currentRoute.value
        ? router.currentRoute.value.fullPath
        : undefined;
      router.push({
        name: 'MyAccount',
        query: { redirect: currentPath, reason: 'session_expired' }
      });

      const sessionError = new Error('Session expired');
      sessionError.code = 'SESSION_EXPIRED';
      sessionError.response = { status: 401, data: { code: 'SESSION_EXPIRED' } };
      return Promise.reject(sessionError);
    }

    // Guest path: refresh the guest token and retry the original request once.
    try {
      const newToken = await refreshGuestToken();
      store.dispatch('user/updateLoggedInUserType', GUEST_LOGGED_IN_USER);
      originalRequest.headers['authorization'] = newToken;
      return instance(originalRequest);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }
);

export default instance;
